import { getThemeForKey, ValueDisplayValue } from "@mcleod/core";
import { BlurEvent, ImageProps } from "../..";
import { ComponentTypes } from "../../base/ComponentTypes";
import { KeyEvent } from "../../events/KeyEvent";
import { RadioBox } from "./RadioBox";
import { RadioBoxOptionStyles } from "./RadioBoxOptionStyles";
import { RadioOption } from "./RadioOption";
import { RadioOptionPropDefinitions, RadioOptionProps } from "./RadioOptionProps";
import { RadioOptionStyles } from "./RadioOptionStyles";

export class RadioBoxOption extends RadioOption {
    constructor(props?: Partial<RadioOptionProps>) {
        super({ themeKey: "radioBoxOption.base", ...props });
        this._element.classList.add(RadioOptionStyles.optionBase);
        this._element.classList.add(RadioBoxOptionStyles.boxOptionBase);
    }

    override get group(): RadioBox {
        return super.group as RadioBox;
    }

    override set group(value: RadioBox) {
        super.group = value;
        this.syncColors();
    }

    public override get valueDisplayValue(): ValueDisplayValue {
        return super.valueDisplayValue;
    }

    public override set valueDisplayValue(value: ValueDisplayValue) {
        super.valueDisplayValue = value;
        const imageProps: Partial<ImageProps> = {
            name: value.getAddlValue("imageName"),
            margin: 0,
            marginRight: 4,
            padding: 0
        }
        this.labelDisplayValue.imageProps = imageProps;
        // The height and width get reset when the options are laid out and sized
        this.labelDisplayValue.imageHeight = 0;
        this.labelDisplayValue.imageWidth = 0;
    }

    syncBorders(isFirst: boolean, isLast: boolean) {
        const borderProps = getThemeForKey("radioBoxOption.border");
        const radius = borderProps.borderRadius;
        this.borderTopLeftRadius = isFirst ? radius : 0;
        this.borderBottomLeftRadius = isFirst ? radius : 0;
        this.borderTopRightRadius = isLast ? radius : 0;
        this.borderBottomRightRadius = isLast ? radius : 0;
        
        const width = borderProps.borderWidth;
        // Setting all of these border widths separately covers up an issue with Component.setElementBorder()
        this.borderLeftWidth = isFirst ? width : 0;
        this.borderTopWidth = width;
        this.borderBottomWidth = width;
        this.borderRightWidth = width;
    }

    private syncColors(selected: boolean = (this === this.group?.selectedOption)) {
        if (this.group == null)
            return;
        if (selected) {
            this.backgroundColor = this.group.selectedBackgroundColor;
            this.labelDisplayValue.color = this.group.selectedTextColor;
            // Setting all of these border colors separately covers up an issue with Component.setElementBorder()
            this.borderLeftColor = this.backgroundColor;
            this.borderRightColor = this.backgroundColor;
            this.borderTopColor = this.backgroundColor;
            this.borderBottomColor = this.backgroundColor;
        }
        else {
            // Setting all of these border colors separately covers up an issue with Component.setElementBorder()
            this.backgroundColor = this.group.unselectedBackgroundColor;
            this.labelDisplayValue.color = this.group.unselectedTextColor;
            const color = getThemeForKey("radioBoxOption.unselectedBorderColor");
            this.borderLeftColor = color;
            this.borderRightColor = color;
            this.borderTopColor = color;
            this.borderBottomColor = color;
        }
    }

    override getFocusTarget(): HTMLElement {
        return this.labelDisplayValue?._element;
    }

    public override getEventTarget(): HTMLElement {
        return this.labelDisplayValue?._element;
    }

    override set selected(value: boolean) {
        super.selected = value;
        this.syncColors(value);
    }

    protected override createDisplayValueLabel() {
        super.createDisplayValueLabel();
        this.labelDisplayValue.allowSelect = false;
        this.labelDisplayValue._element.tabIndex = 0;
        this._syncEnabled();
        this.labelDisplayValue.addKeyDownListener((event: KeyEvent) => this.handleSelectionViaKey(event));
        this.labelDisplayValue.addBlurListener((event: BlurEvent) => this.group.evaluateOptionBlur(event));
    }

    override enable() {
        const labelElement = this.labelDisplayValue?._element;
        if (labelElement != null) {
            labelElement.removeAttribute("disabled");
            labelElement.setAttribute("tabindex", this.focusable === true ? "0" : "-1");
            labelElement.classList.remove(RadioBoxOptionStyles.labelDisabled);
            labelElement.classList.add(RadioBoxOptionStyles.label);
            this.setProps({ ...getThemeForKey("radioBoxOption.enabled") });
            this.syncColors();
        }
    }

    override disable() {
        const labelElement = this.labelDisplayValue?._element;
        if (labelElement != null) {
            labelElement.setAttribute("disabled", "true");
            labelElement.setAttribute("tabindex", "-1");
            labelElement.classList.remove(RadioBoxOptionStyles.label);
            labelElement.classList.add(RadioBoxOptionStyles.labelDisabled);
            this.setProps({ ...getThemeForKey("radioBoxOption.disabled") });
        }
    }

    override getPropertyDefinitions() {
        return RadioOptionPropDefinitions.getDefinitions();
    }

    override get serializationName() {
        return "radioboxoption";
    }

    override get properName(): string {
        return "Radio Box Option";
    }

    override get shortName(): string {
        return "boxoption";
    }

    override getBasicValue(): any {
        return this.value;
    }
}
ComponentTypes.registerComponentType("radioboxoption", { constructor: RadioBoxOption.prototype.constructor });
